/**
 * Selected-model preference — the operator's choice of which catalogued LLM
 * the agent worker should use. Persisted in localStorage so it survives
 * reloads; read by `argoWorker` on every call and sent to the worker.
 *
 * Deployment note: Argo ships as siloed single-tenant instances, so a
 * build-time default can be baked per instance via `VITE_LLM_MODEL`. The
 * runtime picker (Admin → Settings) overrides that default for this browser.
 * If a future instance wants an org-shared choice, promote this to a Firestore
 * settings doc — the read/write seam here is the only thing that changes.
 */

import { DEFAULT_MODEL_ID, isKnownModel } from './models';

const STORAGE_KEY = 'argo.llm.model';

/** The per-instance build default, if a valid one was set in the deploy env. */
function envDefault(): string {
  const fromEnv = import.meta.env.VITE_LLM_MODEL as string | undefined;
  return fromEnv && isKnownModel(fromEnv) ? fromEnv : DEFAULT_MODEL_ID;
}

/** The model id the agent worker should use right now. Always a known id. */
export function getSelectedModel(): string {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (stored && isKnownModel(stored)) return stored;
  } catch {
    // localStorage unavailable (SSR / private mode) — fall through.
  }
  return envDefault();
}

/** Persist the operator's model choice. Ignores unknown ids defensively. */
export function setSelectedModel(id: string): void {
  if (!isKnownModel(id)) return;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, id);
  } catch {
    // Best-effort — a failed write just means we fall back to the default.
  }
}
